// Cross-subdomain session handoff.
//
// The suite is several separate SPAs on sibling hosts of one domain —
// backoffice.liftconnectpro.dev, devices.liftconnectpro.dev,
// devices.qc.f2core.com, devices.f2core.com. Each keeps its session in
// `localStorage.authToken`, and localStorage is scoped per ORIGIN, so a link
// from one app to another landed on a login page even though the user had
// just signed in next door.
//
// So the access token is mirrored into a cookie on the PARENT domain, which
// every sibling host can read, and each app seeds its own localStorage from
// that cookie at startup when it has no token of its own.
//
// The organization the session is working in goes with it, in a second
// cookie. The token says who the user is, not which organization: on a host
// that is not an organization (backoffice, devices) only the sign-in response
// said that, and it is kept in the signing-in origin's localStorage._subdomain.
// Without it the app next door could only send a placeholder `_subdomain`,
// and flex2-api refused every request a non-admin made there.
//
// Tradeoff, deliberately taken: the token becomes readable by script on every
// sibling subdomain rather than just the one that obtained it. They are all
// our apps and all already hold tokens for the same user, and the cookie is
// Secure + SameSite=Lax and expires with the token. What it is NOT is a
// hardening measure — it is exactly as exposed as localStorage, to one more
// host. HttpOnly is not an option: the apps send the token as an
// Authorization header from JavaScript, so JavaScript has to be able to read
// it.
//
// Keep this file identical in flex2-webapp and devices-webapp.

const COOKIE_NAME = "f2AuthToken";
const TENANT_COOKIE_NAME = "f2AuthTenant";

// Host labels that never name an organization: the platform admin host, the
// common sign-in hosts and the product hosts. A session sending one of these
// (a super admin's, or one with no tenant yet) has no organization to hand on.
const NOT_AN_ORGANIZATION = new Set(["backoffice", "account", "app", "devices", "energy", "www"]);

// A token with no readable expiry still gets a bounded cookie rather than a
// session-length one, so a shared machine doesn't keep it indefinitely.
const FALLBACK_MAX_AGE_SECONDS = 8 * 60 * 60;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

/**
 * The domain to scope the cookie to: the current host minus its first label.
 *
 * Returns null — meaning "don't use a shared cookie here" — for anything where
 * that would be wrong or useless:
 *
 *   localhost, 127.0.0.1, an IP        no parent domain to share with
 *   f2core.com (apex, two labels)      stripping would give the public suffix
 *                                      "com", which every browser rejects
 *
 * backoffice.liftconnectpro.dev -> .liftconnectpro.dev
 * devices.qc.f2core.com         -> .qc.f2core.com
 */
export function sharedCookieDomain() {
  if (!isBrowser()) return null;

  const host = window.location.hostname || "";
  // Bare IPv4/IPv6 or a single-label host (localhost) can't carry a domain
  // cookie for siblings, because it has none.
  if (!host.includes(".") || /^[\d.]+$/.test(host) || host.includes(":")) return null;

  const labels = host.split(".");
  // Three labels minimum: the remainder after dropping one must still be a
  // real registrable domain, not a public suffix.
  if (labels.length < 3) return null;

  return `.${labels.slice(1).join(".")}`;
}

/** The JWT's claims, or null. The payload is base64url, which atob can't read as is. */
function tokenClaims(token) {
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part + "=".repeat((4 - (part.length % 4)) % 4)));
  } catch (_e) {
    return null;
  }
}

/** Seconds until the JWT's `exp`, or null if it has none we can read. */
function secondsUntilExpiry(token) {
  const exp = tokenClaims(token)?.exp;
  // Not a JWT, or not decodable — the caller falls back to a fixed window.
  if (!exp) return null;
  const remaining = Math.floor(exp - Date.now() / 1000);
  return remaining > 0 ? remaining : null;
}

function writeCookie(name, value, maxAge) {
  const domain = sharedCookieDomain();
  if (!domain) return;

  // Secure only over https: a Secure cookie set on http is dropped silently,
  // which would make this look like it works and quietly do nothing.
  const secure = window.location.protocol === "https:" ? "; secure" : "";

  // Lax rather than Strict: sibling hosts of one registrable domain are
  // same-site either way, and Lax survives the top-level navigations a
  // cross-app link involves.
  document.cookie =
    `${name}=${encodeURIComponent(value)}; domain=${domain}; path=/; ` +
    `max-age=${maxAge}; samesite=lax${secure}`;
}

function readCookie(name) {
  if (!isBrowser()) return null;

  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  if (!match) return null;

  return decodeURIComponent(match.slice(name.length + 1)).trim();
}

function dropCookie(name) {
  const domain = sharedCookieDomain();
  if (!domain) return;

  document.cookie = `${name}=; domain=${domain}; path=/; max-age=0; samesite=lax`;
}

/**
 * Mirror the token to the parent domain, with `tenant`: the `_subdomain` this
 * app sends with it. No-op where that isn't possible.
 */
export function writeSharedAuthToken(token, tenant) {
  if (!sharedCookieDomain() || !token || typeof token !== "string") return;

  writeCookie(COOKIE_NAME, token, secondsUntilExpiry(token) ?? FALLBACK_MAX_AGE_SECONDS);
  writeSharedTenant(token, tenant);
}

/**
 * Mirror the organization `token`'s session is working in. Tied to the token's
 * user (`sub`), so a sibling holding another user's token never adopts it; a
 * tenant that is not an organization clears it instead.
 */
export function writeSharedTenant(token, tenant) {
  if (!sharedCookieDomain()) return;

  const sub = typeof token === "string" ? tokenClaims(token)?.sub : null;
  const name = typeof tenant === "string" ? tenant.trim() : "";
  if (!sub || !name || NOT_AN_ORGANIZATION.has(name.toLowerCase())) {
    dropCookie(TENANT_COOKIE_NAME);
    return;
  }
  writeCookie(
    TENANT_COOKIE_NAME,
    JSON.stringify({ sub, tenant: name }),
    secondsUntilExpiry(token) ?? FALLBACK_MAX_AGE_SECONDS
  );
}

export function readSharedAuthToken() {
  const raw = readCookie(COOKIE_NAME);
  // Same placeholder guard as utils/authToken: "undefined"/"null" are truthy
  // strings that would gate a bootstrap call into a 401.
  if (!raw || raw === "undefined" || raw === "null") return null;
  return raw;
}

/** The organization a sibling shared for `token`'s user, or null. */
export function readSharedTenant(token) {
  const sub = typeof token === "string" ? tokenClaims(token)?.sub : null;
  const raw = readCookie(TENANT_COOKIE_NAME);
  if (!sub || !raw) return null;

  try {
    const shared = JSON.parse(raw);
    return shared?.sub === sub && typeof shared.tenant === "string" && shared.tenant
      ? shared.tenant
      : null;
  } catch (_e) {
    return null;
  }
}

/**
 * Drop the shared cookies. Must happen on EVERY path that clears the local
 * session — sign-out and the 401 handler — or the next page load seeds
 * localStorage straight back from a dead token and loops.
 */
export function clearSharedAuthToken() {
  dropCookie(COOKIE_NAME);
  dropCookie(TENANT_COOKIE_NAME);
}

/**
 * Adopt a sibling app's session when this origin has none, and its
 * organization when this origin has a session for the same user but no
 * organization of its own (a token picked up here before the organization
 * was shared).
 *
 * Call before the store reads localStorage. Never overwrites a token or an
 * organization this origin already has: that one may be newer (an org switch
 * or impersonation here), and the cookies are only a fallback.
 */
export function seedAuthTokenFromSharedCookie() {
  if (!isBrowser()) return null;

  try {
    const existing = localStorage.getItem("authToken");
    const usable = typeof existing === "string" && existing.trim()
      && existing !== "undefined" && existing !== "null";
    const shared = usable ? null : readSharedAuthToken();
    if (shared) localStorage.setItem("authToken", shared);

    const token = usable ? existing : shared;
    if (token && !localStorage.getItem("_subdomain")) {
      const tenant = readSharedTenant(token);
      if (tenant) localStorage.setItem("_subdomain", tenant);
    }
    return shared;
  } catch (_e) {
    // private mode / storage disabled — the app falls back to its login page
    return null;
  }
}
